import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { getSaleReportDailyGrid } from "../../../services";
import { DailyGridResponse } from "../../../types/sale-daily-grid.type";

export type DailyGridStatus = "loading" | "success" | "error";

interface UseDailyGridResult {
  status: DailyGridStatus;
  /** Parsed payload (response.data.data). `null` = API returned data:null. */
  data: DailyGridResponse | null;
  /** Bumped by `retry()` to re-run the request. */
  refetch: () => void;
}

/**
 * Fetches the read-only daily-grid for a sale report. Mirrors the project's
 * envelope pattern (real payload at response.data.data) and relies on the
 * shared axios interceptors for auth + the global error toast; this hook only
 * tracks the page-level loading/error/empty state so the page can render the
 * right skeleton/empty/error UI.
 */
export function useDailyGrid(
  uuid: string | undefined,
  version: string | null,
): UseDailyGridResult {
  const [status, setStatus] = useState<DailyGridStatus>("loading");
  const [data, setData] = useState<DailyGridResponse | null>(null);
  const [trigger, setTrigger] = useState<number>(0);

  const refetch = useCallback(() => setTrigger((n) => n + 1), []);

  useEffect(() => {
    if (!uuid) {
      setStatus("error");
      return;
    }

    let cancelled = false;
    setStatus("loading");

    getSaleReportDailyGrid(uuid, version ?? undefined)
      .then((res) => {
        if (cancelled) return;
        // Envelope: { statusCode, ..., data: DailyGridResponse | null }
        setData(res.data?.data ?? null);
        setStatus("success");
      })
      .catch((err) => {
        if (cancelled || axios.isCancel(err)) return;
        setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, [uuid, version, trigger]);

  return { status, data, refetch };
}
